import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toastSuccess, toastError } from '../../../utils/toast';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const inputStyle = { width: '100%', minHeight: '48px', padding: '0 20px', background: '#FBF9D9', border: 'none', borderRadius: '999px', fontFamily: 'Poppins, sans-serif', fontSize: '14.5px', color: '#051004', outline: 'none' };
const textareaStyle = { width: '100%', padding: '14px 20px', background: '#FBF9D9', border: 'none', borderRadius: '20px', fontFamily: 'Poppins, sans-serif', fontSize: '14.5px', color: '#051004', outline: 'none', resize: 'vertical' };
const labelStyle = { display: 'block', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', marginBottom: '8px' };
const errorStyle = { margin: '6px 0 0', fontSize: '12.5px', color: '#A32C0F' };

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    aria-pressed={checked}
    style={{ border: 'none', cursor: 'pointer', width: '50px', height: '28px', borderRadius: '999px', padding: '3px', display: 'flex', background: checked ? '#72B744' : 'rgba(5,16,4,0.22)', transition: 'background 180ms ease', flexShrink: 0 }}
  >
    <span style={{ width: '22px', height: '22px', borderRadius: '999px', background: '#FCFBF5', transform: checked ? 'translateX(22px)' : 'translateX(0)', transition: 'transform 180ms ease' }} />
  </button>
);

const SupplementModal = ({ isOpen, onClose, supplement, onSuccess, dishes, preselectedDishId = null, existingSupplements = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    dish_id: preselectedDishId ? preselectedDishId.toString() : '',
    is_available: true
  });
  const [selectedExistingId, setSelectedExistingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const existingList = Array.isArray(existingSupplements) ? existingSupplements : [];

  useEffect(() => {
    if (supplement) {
      setFormData({
        name: supplement.name || '',
        description: supplement.description || '',
        price: supplement.price ? supplement.price.toString() : '',
        dish_id: supplement.dish_id ? supplement.dish_id.toString() : '',
        is_available: supplement.is_available !== false
      });
      setSelectedExistingId('');
    } else {
      setFormData({
        name: '',
        description: '',
        price: '',
        dish_id: preselectedDishId ? preselectedDishId.toString() : '',
        is_available: true
      });
      setSelectedExistingId('');
    }
    setErrors({});
  }, [supplement, isOpen, preselectedDishId]);

  const handleSelectExisting = (e) => {
    const id = e.target.value;
    setSelectedExistingId(id);
    if (!id) {
      setFormData(prev => ({ ...prev, name: '', description: '', price: '' }));
      return;
    }
    const existing = existingList.find(s => s.id === parseInt(id, 10));
    if (existing) {
      setFormData(prev => ({
        ...prev,
        name: existing.name || '',
        description: existing.description || '',
        price: existing.price != null ? String(existing.price) : ''
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Le prix doit être supérieur à 0';
    }
    if (!formData.dish_id) {
      newErrors.dish_id = 'Veuillez sélectionner un plat';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toastError('Veuillez renseigner le nom, le prix et sélectionner un plat.');
      return;
    }

    setLoading(true);
    try {
      const supplementData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        dish_id: parseInt(formData.dish_id),
        is_available: formData.is_available
      };

      if (supplement) {
        await onSuccess(supplement.id, supplementData);
        toastSuccess('Supplément mis à jour avec succès.');
      } else {
        await onSuccess(supplementData);
        toastSuccess('Supplément créé avec succès.');
      }
      onClose();
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      setErrors({ submit: error.message || 'Une erreur est survenue' });
      toastError(error.response?.data?.detail || error.message || 'Erreur lors de la sauvegarde du supplément.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(5,16,4,0.5)' }}>
      <div style={{ background: '#FCFBF5', borderRadius: '28px', maxWidth: '560px', width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(5,16,4,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '22px 26px', borderBottom: '1px solid rgba(5,16,4,0.08)', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>
            {supplement ? 'Modifier le supplément' : 'Ajouter un supplément'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            style={{ flexShrink: 0, border: 'none', cursor: 'pointer', width: '38px', height: '38px', borderRadius: '999px', background: 'transparent', color: 'rgba(5,16,4,0.4)', display: 'grid', placeItems: 'center' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(5,16,4,0.06)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '26px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          {errors.submit && (
            <div style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '16px', padding: '12px 16px', fontSize: '13.5px' }}>
              {errors.submit}
            </div>
          )}

          {!supplement && existingList.length > 0 && (
            <div>
              <label style={labelStyle}>Utiliser un supplément existant</label>
              <select value={selectedExistingId} onChange={handleSelectExisting} style={inputStyle}>
                <option value="">Créer un nouveau supplément</option>
                {existingList.map(sup => (
                  <option key={sup.id} value={sup.id}>
                    {sup.name} — {Number(sup.price).toFixed(2)} €
                  </option>
                ))}
              </select>
              <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'rgba(5,16,4,0.55)' }}>
                Choisir un supplément existant préremplit le nom et le prix. Vous pouvez les modifier avant d&apos;ajouter.
              </p>
            </div>
          )}

          <div>
            <label style={labelStyle}>Nom du supplément *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              style={inputStyle}
              placeholder="Ex: Extra fromage, Sauce supplémentaire..."
            />
            {errors.name && <p style={errorStyle}>{errors.name}</p>}
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              style={textareaStyle}
              placeholder="Description du supplément..."
            />
          </div>

          <div>
            <label style={labelStyle}>Plat associé *</label>
            <select name="dish_id" value={formData.dish_id} onChange={handleChange} style={inputStyle}>
              <option value="">Sélectionner un plat</option>
              {dishes && Array.isArray(dishes) && dishes.map(dish => (
                <option key={dish.id} value={dish.id}>
                  {dish.name}
                </option>
              ))}
            </select>
            {errors.dish_id && <p style={errorStyle}>{errors.dish_id}</p>}
          </div>

          <div>
            <label style={labelStyle}>Prix (€) *</label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              step="0.01"
              min="0"
              style={inputStyle}
              placeholder="0.00"
            />
            {errors.price && <p style={errorStyle}>{errors.price}</p>}
          </div>

          <div className="flex items-center gap-3">
            <Toggle checked={formData.is_available} onChange={(v) => setFormData(prev => ({ ...prev, is_available: v }))} />
            <span style={{ fontSize: '14px', fontWeight: 600 }}>Disponible</span>
          </div>

          <div className="flex justify-end gap-2.5 flex-wrap" style={{ paddingTop: '18px', borderTop: '1px solid rgba(5,16,4,0.08)' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: PRIMARY, fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '46px', padding: '0 22px', borderRadius: '999px' }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '46px', padding: '0 24px', borderRadius: '999px', opacity: loading ? 0.6 : 1 }}
            >
              {loading ? 'Enregistrement...' : supplement ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupplementModal;
